import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getCatalog } from '@/api/adapter/onboarding';
import { getProjectView } from '@/api/adapter/projectViews';
import { getProjectParticipants } from '@/api/adapter/projectParticipants';
import { profileImageUrl } from '@/hooks/useProfile';
import { useAuthStore } from '@/stores/authStore';
import { useDaoConfigStore } from '@/stores/daoConfigStore';
import {
  completeProjectCommand,
  clearProjectCommand,
  pendingProjectCommand,
} from '@/services/projectCommands';
import type {
  VotesResponse,
  SubmitCoordinatorRatingsInput,
  SubmitDeveloperRatingInput,
} from '@/types';

export const voteKeys = {
  all: ['votes'] as const,
  members: (id: string) => ['votes', 'members', id] as const,
};
export function useVoteMembers(projectId: string | undefined) {
  const principal = useAuthStore((s) => s.session?.principalId);
  return useQuery<VotesResponse>({
    queryKey: [...voteKeys.members(projectId ?? ''), principal],
    enabled: !!projectId && !!principal,
    queryFn: async () => {
      const [project, participants, catalog] = await Promise.all([
        getProjectView(projectId!),
        getProjectParticipants(projectId!),
        getCatalog(),
      ]);
      return {
        project: {
          id: project.projectId,
          title: project.title,
          state: project.completed ? 'Completed' : 'InProgress',
        },
        members: participants.workers.map((w) => ({
          name: w.profiles?.worker?.name ?? w.displayName ?? w.accountId,
          role:
            [
              ...new Set(
                project.proposals.flatMap((p) =>
                  p.milestones.flatMap((m) =>
                    m.assignments
                      .filter((a) => a.worker === w.accountId)
                      .map(
                        (a) =>
                          catalog.roles.find(
                            (r) =>
                              r.id ===
                              m.definition.requirements.find(
                                (r) => r.key === a.requirementKey
                              )?.roleId
                          )?.name
                      )
                      .filter((name): name is string => !!name)
                  )
                )
              ),
            ].join(', ') ||
            useDaoConfigStore.getState().config.terminology.workerRole ||
            'Developer',
          proficiency: null,
          userId: w.accountId,
          email: w.contactEmail,
          imageUrl:
            w.workerImage && w.profiles
              ? profileImageUrl(w.profiles.principalId, 'worker')
              : '/images/none.png',
        })),
      };
    },
  });
}
async function submit(
  projectId: string,
  ratings: { account: string; score: number }[]
) {
  const principal = useAuthStore.getState().session?.principalId;
  if (!principal) throw new Error('Log in before submitting ratings.');
  const pending = pendingProjectCommand(principal, projectId);
  if (pending && pending.command.kind !== 'project-evaluate')
    throw new Error('Another project request is pending. Resolve it first.');
  const result = await completeProjectCommand(
    principal,
    pending
      ? null
      : { kind: 'project-evaluate', projectId, payload: { ratings } },
    projectId,
    () => {},
    new AbortController().signal
  );
  if (!result)
    throw new Error(
      'The evaluation is pending. Retry to check the saved request.'
    );
  if (useAuthStore.getState().session?.principalId !== principal)
    throw new Error('Your account changed. Log in again.');
  clearProjectCommand(principal, projectId);
  return { projectId, message: 'Ratings saved.' };
}
function useEvaluationMutation<T>(
  build: (input: T) => Promise<{ projectId: string; message: string }>
) {
  const cache = useQueryClient();
  return useMutation({
    mutationFn: build,
    onSuccess: async () => {
      await cache.invalidateQueries({ queryKey: ['current-projects'] });
      await cache.invalidateQueries({ queryKey: voteKeys.all });
    },
  });
}
export interface SubmitVotesInput {
  projectId: string;
  coordinatorRating: number;
}
export function useSubmitVotes() {
  return useEvaluationMutation(async (input: SubmitVotesInput) => {
    const project = await getProjectView(input.projectId);
    return submit(input.projectId, [
      { account: project.coordinator, score: input.coordinatorRating },
    ]);
  });
}
export function useSubmitCoordinatorRatings() {
  return useEvaluationMutation(async (input: SubmitCoordinatorRatingsInput) => {
    const project = await getProjectView(input.projectId);
    return submit(input.projectId, [
      { account: project.client, score: input.clientRating },
      ...input.teamRatings.map(([account, score]) => ({ account, score })),
    ]);
  });
}
export function useSubmitDeveloperRating() {
  return useEvaluationMutation(async (input: SubmitDeveloperRatingInput) => {
    const project = await getProjectView(input.projectId);
    return submit(input.projectId, [
      { account: project.coordinator, score: input.coordinatorRating },
    ]);
  });
}
